import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  KeyValuePairs,
  Tabs,
  Grid,
  Alert,
  Flashbar,
  Pagination,
  Badge,
  StatusIndicator,
} from '@cloudscape-design/components';
import { V2Adapter } from 'src/v2/desktop/V2Adapter';
import { AuditInsightsTab } from 'src/v2/features/audit-insights/components/AuditInsightsTab';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuditSummary from 'src/hooks/useAuditSummary';
import ScoreBreakdown from 'src/components/audit/ScoreBreakdown';
import TasksTable from 'src/components/task/TasksTable';
import QuestionSummaryTable from 'src/components/audit/QuestionSummaryTable';
import {
  getAuditSummaryDefinition,
  flattenAuditDataForExport,
  getAuditSummaryExportConfig,
} from './auditSummaryConfig';
import { ExportButton } from 'src/components/ExportButton';
import QuestionsTable from 'src/components/audit/QuestionsTable';
import ReopenAuditModal from 'src/components/audit/ReopenAuditModal';
import CIProjectModal from 'src/components/audit/CIProjectModal';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import { isAuditInsightsPilotUser } from 'src/context/auditInsightsPilot';
import { useUserAlias } from 'src/data/api/orbit-service-ts/user/queries/useUserAlias';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useUserProfile from 'src/hooks/useUserProfile';
import {
  AUDIT_STATUS_LABELS,
  AuditStatus,
  CI_PORTAL_URL,
  ScoreFilterType,
} from 'src/contstant/commonLabels';
import { getCIModalStorageKey } from 'src/contstant/storageKeys';
import { emit } from 'src/v2/shared/observability/emit';
import { getFromLocalStorage, saveToLocalStorage } from 'src/utils/localStorageUtils';
import {
  formatDateTime,
  getAuditStatusColor,
  getAuditStatusIcon,
} from 'src/utils/generalUtils';
import { useTasks } from 'src/store/tasks/useTasks';
import { useUsers } from 'src/store/users/useUsers';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import routes from 'src/contstant/routePaths';
import { round } from 'lodash';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import { getIsPerformAudit, getIsReopenAudit } from 'src/helper/performAuditHelpers';
import {
  toInsightQuestions,
  toResponseBreakdown,
} from 'src/v2/features/audit-insights/logic/insightsQuestionMapper';
import { getPerformAuditUrl } from '../audits/auditsConfig';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { TaskStatus } from 'src/contstant/taskStatusColors';
import { useSplitPanel } from 'src/context/SplitPanelContext';

// Constants for pagination
const QUESTIONS_PER_PAGE = 20; // Number of questions to display per page

const AUDIT_INSIGHTS_TAB_ID = 'audit-insights';
const SUMMARY_TAB_ID = 'summary';

interface AuditSummaryContentProps {
  auditId: string;
  businessUnit: string;
}

/**
 * The wording under the figure in the score block.
 *
 * Three states, not two: an audit mid-sync has a score that has not settled, and reporting
 * that as "not scored" tells the reader something untrue about an audit that does have one.
 *
 * @param isScored - The score is present and the index agrees with it
 * @param isSettling - The audit completed but the index has not caught up yet
 */
function scoreLabel(isScored: boolean, isSettling: boolean): string {
  if (isScored) return 'Score Percent';
  if (isSettling) return 'Score pending';
  return 'Not scored yet';
}

/**
 * AuditSummaryContent - Displays comprehensive audit information including summary, questions, and tasks
 * Shows audit details, score breakdown, question responses, and associated tasks
 *
 * @param auditId - Audit to display
 * @param businessUnit - Business unit that owns the audit
 */
const AuditSummaryContent = ({ auditId, businessUnit }: AuditSummaryContentProps) => {
  // Navigation and routing
  const navigate = useNavigate();

  const { enableEnhancedResponseTypes, enableAuditInsights: insightsFlag } =
    useFeatureFlags();
  const [userAlias] = useUserAlias();
  // The flag stays off in PROD; the pilot list opens the tab for its own users there.
  const enableAuditInsights = insightsFlag || isAuditInsightsPilotUser(userAlias);

  // CI project prompt state
  const [ciModalVisible, setCiModalVisible] = useState(false); // Controls CI project modal visibility
  const hasShownCiModal = useRef(false); // Prevents modal re-showing after dismiss

  // Custom hooks for data management
  // Note: We don't use Redux auditSummary/currentAudit here - we use displayAuditSummary from polling hook
  const { checkUserPermissions, userPermission } = useUsers();
  const { businessUnitList } = useBusinessUnit();
  const { tasksList, loadTasks, fetchTasksApiStatus, createTaskFromQuestionApiStatus } =
    useTasks();
  const { closeView: closeSplitPanel } = useSplitPanel();
  const {
    getPersona,
    getUserProgramId,
    getUserBusinessUnitId,
    getDivisions,
    getUserId,
    currentUser,
    getPreferLanguageList,
    getUserSelectedLanguage,
  } = useUserProfile();

  // Component state
  // Insights leads and is selected when the flag is on; with the flag off the page opens on
  // Summary exactly as before.
  const [activeTabId, setActiveTabId] = useState(
    enableAuditInsights ? AUDIT_INSIGHTS_TAB_ID : SUMMARY_TAB_ID,
  );

  // Outside prod the dev panel can turn the flag off while its tab is selected. The tab
  // then leaves the strip while activeTabId still names it, and Cloudscape renders no
  // panel body, so fall back to Summary.
  useEffect(() => {
    if (!enableAuditInsights && activeTabId === AUDIT_INSIGHTS_TAB_ID) {
      setActiveTabId(SUMMARY_TAB_ID);
    }
  }, [enableAuditInsights, activeTabId]);
  const [summaryFilter, setSummaryFilter] = useState<string | null>(
    ScoreFilterType.TOTAL,
  );
  const [questionResponseFilter, setQuestionResponseFilter] = useState<string | null>(
    ScoreFilterType.TOTAL,
  );
  const [reopenModalVisible, setReopenModalVisible] = useState(false); // Controls reopen modal visibility

  // Use custom polling hook instead of direct Redux state
  const {
    displayAuditSummary,
    currentAudit: polledCurrentAudit,
    statusesMatch,
    isLoading: pollingIsLoading,
    errorMessage: pollingErrorMessage,
    refreshData,
  } = useAuditSummary({
    businessUnitId: businessUnit,
    auditId: auditId,
    additionalLanguages: getPreferLanguageList(),
  });

  // Use questions from displayAuditSummary (polling hook) which has complete data
  // Memoize based on the entire questions array to ensure updates when any question changes
  const allQuestions = useMemo(() => {
    return displayAuditSummary?.questions || [];
  }, [displayAuditSummary?.questions]);

  const hasLoadedAll = !!displayAuditSummary?.questions;
  const questionsLoading = pollingIsLoading;
  const questionsError = pollingErrorMessage;
  const totalQuestionsLoaded = allQuestions.length;

  /**
   * Filters questions based on selected filter criteria
   * Returns all questions if no filter or 'TOTAL' filter is selected
   */
  const filterQuestionsByResult = useCallback(
    (questions: typeof allQuestions, filter: string | null) => {
      if (!filter || filter === ScoreFilterType.TOTAL) return questions;

      return questions.filter((question) => {
        const result =
          question.responseDetails.mainQuestionResponse.responseResult?.toLowerCase();
        return result === (filter ? filter.toLowerCase() : null);
      });
    },
    [],
  );

  // Filter questions for Summary tab based on selected filter
  const summaryFilteredQuestions = filterQuestionsByResult(allQuestions, summaryFilter);

  // Local pagination for Summary tab (on filtered questions)
  const {
    currentPageIndex: summaryPageIndex,
    setCurrentPageIndex: setSummaryPageIndex,
    paginatedItems: summaryPaginatedQuestions,
    totalPages: summaryTotalPages,
  } = useLocalPagination({
    items: summaryFilteredQuestions,
    pageSize: QUESTIONS_PER_PAGE,
  });

  // Filter questions for Question Response tab based on selected filter
  const questionResponseFilteredQuestions = filterQuestionsByResult(
    allQuestions,
    questionResponseFilter,
  );

  // Local pagination for Question Response tab (on filtered questions)
  const {
    currentPageIndex: questionResponsePageIndex,
    setCurrentPageIndex: setQuestionResponsePageIndex,
    paginatedItems: questionResponsePaginatedQuestions,
    totalPages: questionResponseTotalPages,
  } = useLocalPagination({
    items: questionResponseFilteredQuestions,
    pageSize: QUESTIONS_PER_PAGE,
  });

  const auditMetrics = useMemo(
    () => toResponseBreakdown(displayAuditSummary?.audit?.metrics),
    [displayAuditSummary?.audit?.metrics],
  );

  /**
   * The audit's status, preferring the polled audit over the summary.
   *
   * The two disagree on purpose while the search index catches up: the polled audit comes
   * from the record, the summary from the index. Read once here so the badge and the score
   * block cannot classify the same audit differently — a header that reads "Completed"
   * beside "Not scored yet" is describing two different generations of the same audit.
   */
  const auditStatus = polledCurrentAudit?.status || displayAuditSummary?.audit?.status;

  // Read once and passed on by value. The memo below keys on the language itself rather
  // than on the getter that returns it, so what recomputes the mapping is a reader changing
  // language — not a detail of how `useUserProfile` happens to memoise its getters.
  const selectedLanguage = getUserSelectedLanguage();

  // Mapped only when the tab can show it, since it resolves a translation per question.
  const insightsQuestions = useMemo(
    () => (enableAuditInsights ? toInsightQuestions(allQuestions, selectedLanguage) : []),
    [enableAuditInsights, allQuestions, selectedLanguage],
  );

  // Compute flattened data for export
  const exportData = useMemo(() => {
    if (!displayAuditSummary?.audit) return [];
    return flattenAuditDataForExport(displayAuditSummary.audit, allQuestions);
  }, [displayAuditSummary?.audit, allQuestions]);

  // Check if user has permission to edit any type of audit
  const isPerformAudit = useCallback(() => {
    return getIsPerformAudit(getPersona(), userPermission, displayAuditSummary?.audit);
  }, [getPersona, userPermission, displayAuditSummary?.audit]);

  const isReOpenAuditAllow = useCallback(() => {
    return getIsReopenAudit(getPersona(), userPermission, displayAuditSummary?.audit);
  }, [getPersona, userPermission, displayAuditSummary?.audit]);

  const isPerfectScoreAudit =
    statusesMatch &&
    displayAuditSummary?.audit?.status === AuditStatus.COMPLETED &&
    displayAuditSummary?.audit?.metrics?.scorePercent === 100;

  const emitCIProjectClick = () => {
    emit('ci_project.create_clicked', {
      auditId,
      location: displayAuditSummary?.audit?.location,
      auditType: displayAuditSummary?.audit?.auditType,
      businessUnit,
      userId: getUserId(),
    });
  };

  // Load initial data on component mount - only once
  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- loadData closes over unmemoized store callbacks; adding it refetches every render

  // Close the split panel when navigating away from the audit summary page
  useEffect(() => {
    return () => {
      closeSplitPanel();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- cleanup only — a new context value must not close the panel mid-visit

  // Reset CI modal state when navigating between audits (SPA navigation)
  useEffect(() => {
    setCiModalVisible(false);
    hasShownCiModal.current = false;
  }, [auditId]);

  // Show CI project modal on first load when audit scored 100% and not previously dismissed
  useEffect(() => {
    if (isPerfectScoreAudit && !hasShownCiModal.current) {
      const storageKey = getCIModalStorageKey(auditId, getUserId());
      const stored = getFromLocalStorage<{ completedAt: number } | null>(
        storageKey,
        null,
      );
      const auditCompletedAt = displayAuditSummary?.audit?.completionDate;
      if (!stored || stored.completedAt !== auditCompletedAt) {
        hasShownCiModal.current = true;
        setCiModalVisible(true);
      }
    }
  }, [isPerfectScoreAudit]); // eslint-disable-line react-hooks/exhaustive-deps -- the prompt is offered once per scored audit, not on every id or date change

  // Reload tasks when a new task is created from a question
  useEffect(() => {
    if (createTaskFromQuestionApiStatus.isSuccess) {
      loadTasks({
        businessUnitId: businessUnit,
        auditId: auditId,
        taskStatus: [
          TaskStatus.ASSIGNED,
          TaskStatus.IN_PROGRESS,
          TaskStatus.COMPLETED,
          TaskStatus.CANCELLED,
          TaskStatus.OVERDUE,
        ],
      });
    }
  }, [createTaskFromQuestionApiStatus.isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps -- loadTasks is a new identity each render; adding it reloads in a loop

  // Check user permissions when current user changes
  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [
        permissionAccessConfig.CALIBRATION_AUDIT.PERFORM_CALIBRATION_AUDIT,
        permissionAccessConfig.PROGRESS_AUDIT.PERFORM_PROGRESS_AUDIT,
        permissionAccessConfig.SITUATION_AUDIT.PERFORM_SITUATION_AUDIT,
        permissionAccessConfig.MPA_AUDIT.PERFORM_MPA_AUDIT,
        permissionAccessConfig.CALIBRATION_AUDIT.REOPEN_CALIBRATION_AUDIT,
        permissionAccessConfig.PROGRESS_AUDIT.REOPEN_PROGRESS_AUDIT,
        permissionAccessConfig.SITUATION_AUDIT.REOPEN_SITUATION_AUDIT,
        permissionAccessConfig.MPA_AUDIT.REOPEN_MPA_AUDIT,
      ],
      divisions: getDivisions(),
    });
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps -- the useUserProfile getters are new identities each render

  /**
   * Loads audit summary and associated tasks data
   */
  function loadData() {
    // Use refreshData from the polling hook
    refreshData();
    loadTasks({
      businessUnitId: businessUnit,
      auditId: auditId,
      taskStatus: [
        TaskStatus.ASSIGNED,
        TaskStatus.IN_PROGRESS,
        TaskStatus.COMPLETED,
        TaskStatus.CANCELLED,
        TaskStatus.OVERDUE,
      ],
    });
  }

  /**
   * Renders the audit header with title, metadata, and overall score
   */
  function AuditHeader() {
    return (
      <Grid
        data-testid="audit-header-grid"
        gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}
      >
        <Header
          data-testid="audit-header"
          variant="h1"
          description={
            <>
              <span data-testid="audit-id">
                Audit ID: {displayAuditSummary?.audit.auditId}
              </span>
              {displayAuditSummary?.audit?.completionDate ? (
                <>
                  <span> • </span>
                  <span data-testid="completion-date">
                    Completed on{' '}
                    {formatDateTime(displayAuditSummary?.audit?.completionDate)}
                  </span>
                </>
              ) : (
                <>
                  <span> • </span>
                  <span data-testid="scheduled-date">
                    Scheduled on :{' '}
                    {formatDateTime(displayAuditSummary?.audit?.scheduledDate)}
                  </span>
                </>
              )}

              {displayAuditSummary?.audit?.createdBy && (
                <>
                  <span> • </span>
                  <span data-testid="created-by">
                    By {displayAuditSummary?.audit?.createdBy}
                  </span>
                </>
              )}
            </>
          }
        >
          {displayAuditSummary?.audit.name}
          {renderStatusBadge()}
        </Header>
        {renderScoreBlock()}
      </Grid>
    );
  }

  /**
   * The audit's status, on the title line. Reads the polled audit first so the badge
   * tracks a status change before the summary is refetched, matching the Audit Status
   * field on the Summary tab.
   *
   * Renders inside the h1, which is the only Header slot that sits beside the title —
   * `actions` right-aligns across the whole header instead. Two consequences the
   * wrapper handles or accepts: the badge would inherit the heading's size and weight,
   * so the `Box` resets both, and the heading's accessible name carries the status
   * along with the audit name.
   *
   * Part of the Audit Insights chrome, so it is absent while that flag is off.
   */
  function renderStatusBadge(): JSX.Element | null {
    const status = auditStatus;
    if (!enableAuditInsights || !status) return null;

    return (
      <Box
        variant="span"
        fontSize="body-m"
        fontWeight="normal"
        padding={{ left: 'm' }}
        data-testid="audit-status-slot"
      >
        <StatusIndicator
          data-testid="audit-status-badge"
          type={getAuditStatusIcon(status)}
          colorOverride={getAuditStatusColor(status)}
        >
          {AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}
        </StatusIndicator>
      </Box>
    );
  }

  /**
   * The audit's own score, served verbatim and never recomputed.
   *
   * With the Audit Insights chrome on, the block renders in both states so the
   * header keeps its shape and an unscored audit says so. With the flag off it
   * appears only on a scored audit, as it always has.
   */
  function renderScoreBlock(): JSX.Element | null {
    const scorePercent = displayAuditSummary?.audit?.metrics?.scorePercent;
    // The same status the badge reads. While `statusesMatch` holds, the polled audit and the
    // summary agree by definition, so this only differs mid-sync — which is exactly when
    // taking the summary's word for it would call a finished audit unscored.
    const isCompleted = auditStatus === AuditStatus.COMPLETED;
    // The figure is only trustworthy once the search index agrees with the recorded
    // responses. Until it does, the polling hook is deliberately serving the pre-poll
    // summary, whose score may not be the final one.
    // Narrowed on the type rather than on `!== undefined`. The field is declared a required
    // number, so a null can only arrive from a malformed payload — and `round(null ?? 0)`
    // would render that as a confident 0%.
    const isScored = typeof scorePercent === 'number' && isCompleted && statusesMatch;
    // A completed audit whose index is still catching up has a score that has not settled,
    // which is a different thing from an audit that was never scored.
    const isSettling = isCompleted && !statusesMatch;
    // Bails with the badge rather than outliving it. Before either audit source resolves
    // there is no status to classify, and "Not scored yet" would be a claim about an audit
    // the page has not read yet.
    if (!auditStatus) return null;
    if (!isScored && !enableAuditInsights) return null;

    const value = isScored ? `${round(scorePercent ?? 0, 2)}%` : '—';
    const label = scoreLabel(isScored, isSettling);
    const color = isScored ? 'text-status-warning' : 'text-status-inactive';

    return (
      <Box
        data-testid="overall-score-container"
        float="right"
        padding={{ horizontal: 'xs' }}
      >
        <SpaceBetween
          data-testid="score-spacer"
          direction="vertical"
          size="xs"
          alignItems="center"
        >
          <Box data-testid="score-box" padding="xs">
            <SpaceBetween data-testid="score-content" direction="vertical" size="xxxs">
              <Box
                data-testid="score-percentage"
                textAlign="center"
                fontSize="display-l"
                color={color}
              >
                {value}
              </Box>
              <Box data-testid="score-label" color={color}>
                {label}
              </Box>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  // Loading state
  if (pollingIsLoading) {
    return (
      <Flashbar
        data-testid="loading-flashbar"
        items={[
          {
            type: 'info',
            loading: true,
            header: 'Loading in Progress',
            content: 'Loading details. This may take a few moments.',
            id: 'loading',
          },
        ]}
      />
    );
  }

  // Error state
  if (pollingErrorMessage) {
    return (
      <Alert data-testid="error-alert" type="error">
        Error loading summary details: {pollingErrorMessage}
      </Alert>
    );
  }

  function openPerformAuditPage(auditId: string, businessUnit: string) {
    const params = new URLSearchParams({
      auditId: auditId,
      businessUnit: businessUnit,
    });
    navigate(`${routes.PERFORM_AUDIT.path}?${params}`);
  }

  return (
    <Box data-testid="audit-summary-page" margin={{ top: 's' }}>
      <SpaceBetween data-testid="audit-summary-content" size="m">
        <Container data-testid="audit-header-container" header={<AuditHeader />}>
          <SpaceBetween data-testid="audit-actions" size="m" direction="horizontal">
            {/* Export button - exports audit summary with all questions and sub-questions */}

            {/* Reopen audit button - only enabled for completed/expired audits and when states match */}
            {isReOpenAuditAllow() && (
              <Button
                data-testid="reopen-audit-button"
                variant="primary"
                iconName="refresh"
                disabled={
                  !statusesMatch ||
                  (displayAuditSummary?.audit?.status !== AuditStatus.COMPLETED &&
                    displayAuditSummary?.audit?.status !== AuditStatus.EXPIRED)
                }
                onClick={() => setReopenModalVisible(true)}
              >
                Reopen Audit
              </Button>
            )}
            {/* Perform audit button - only shown for users with edit permissions on non-completed audits */}
            {isPerformAudit() &&
              statusesMatch &&
              (displayAuditSummary?.audit?.status === AuditStatus.NOT_STARTED ||
                displayAuditSummary?.audit?.status === AuditStatus.IN_PROGRESS) && (
                <Button
                  data-testid="perform-audit-button"
                  variant="primary"
                  iconName="file-open"
                  href={getPerformAuditUrl(displayAuditSummary.audit)}
                  onClick={(event) => {
                    event.preventDefault();
                    openPerformAuditPage(auditId, businessUnit);
                  }}
                >
                  Perform Audit
                </Button>
              )}

            {statusesMatch &&
              displayAuditSummary?.audit?.status === AuditStatus.COMPLETED && (
                <Button
                  data-testid="view-audit-button"
                  variant="primary"
                  iconName="file-open"
                  href={getPerformAuditUrl(displayAuditSummary.audit)}
                  onClick={(event) => {
                    event.preventDefault();
                    openPerformAuditPage(auditId, businessUnit);
                  }}
                >
                  View Audit
                </Button>
              )}
            <ExportButton
              buttonVariant="normal"
              data-testid="export-audit-summary-button"
              data={exportData}
              config={getAuditSummaryExportConfig(
                displayAuditSummary?.audit?.auditId,
                displayAuditSummary?.audit?.businessUnit,
                enableEnhancedResponseTypes,
              )}
              buttonText="Export"
              disabled={!statusesMatch || pollingIsLoading}
            />
            {isPerfectScoreAudit && (
              <Button
                data-testid="ci-project-inline-button"
                variant="primary"
                iconName="suggestions"
                iconAlign="left"
                href={CI_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={emitCIProjectClick}
              >
                Explore CI Project
              </Button>
            )}
          </SpaceBetween>
          {isPerfectScoreAudit && (
            <Box data-testid="ci-project-banner" margin={{ top: 's' }}>
              <SpaceBetween direction="horizontal" size="xs">
                <span aria-hidden="true">↗</span>
                <span>
                  Perfect score! Start a Continuous Improvement project to capture the
                  next opportunity for {displayAuditSummary?.audit?.location}.
                </span>
              </SpaceBetween>
            </Box>
          )}
          <ReopenAuditModal
            data-testid="reopen-audit-modal"
            visible={reopenModalVisible}
            onDismiss={() => setReopenModalVisible(false)}
            auditId={auditId}
            businessUnitId={businessUnit}
            onSuccess={() => {
              setReopenModalVisible(false);
              openPerformAuditPage(auditId, businessUnit);
            }}
          />
          <CIProjectModal
            visible={ciModalVisible && isPerfectScoreAudit}
            onDismiss={() => {
              setCiModalVisible(false);
              saveToLocalStorage(getCIModalStorageKey(auditId, getUserId()), {
                completedAt: displayAuditSummary?.audit?.completionDate,
              });
            }}
            onCreateClick={emitCIProjectClick}
            locationName={displayAuditSummary?.audit?.location || ''}
          />
        </Container>

        {/* Main content tabs */}
        <Tabs
          data-testid="audit-summary-tabs"
          activeTabId={activeTabId}
          onChange={({ detail }) => {
            setActiveTabId(detail.activeTabId);
            if (detail.activeTabId !== 'question-response') {
              closeSplitPanel();
            }
          }}
          tabs={[
            // Stencil content inside a Cloudscape tab, so it needs V2Adapter for the design
            // tokens, translations and auth bridge. Same wrapping as AuditsPageGate.
            ...(enableAuditInsights
              ? [
                  {
                    id: AUDIT_INSIGHTS_TAB_ID,
                    label: (
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <span>Audit Insights</span>
                        <Badge color="blue">AI Generated</Badge>
                      </SpaceBetween>
                    ),
                    content: (
                      <V2Adapter>
                        <AuditInsightsTab
                          businessUnitId={businessUnit}
                          auditId={auditId}
                          questions={insightsQuestions}
                          language={selectedLanguage}
                          {...(auditMetrics ? { breakdown: auditMetrics } : {})}
                          {...(displayAuditSummary?.audit
                            ? { audit: displayAuditSummary.audit }
                            : {})}
                          {...(auditStatus ? { auditStatus } : {})}
                        />
                      </V2Adapter>
                    ),
                  },
                ]
              : []),
            {
              id: SUMMARY_TAB_ID,
              label: 'Summary',
              content: (
                <SpaceBetween data-testid="summary-tab-content" size="m">
                  {/* Audit Details section */}
                  {displayAuditSummary?.audit && (
                    <Container
                      data-testid="audit-details-container"
                      header={
                        <Header data-testid="audit-details-header" variant="h3">
                          Audit Details
                        </Header>
                      }
                    >
                      <KeyValuePairs
                        data-testid="audit-details-keyvalue"
                        columns={3}
                        items={getAuditSummaryDefinition(
                          displayAuditSummary,
                          businessUnitList,
                          polledCurrentAudit,
                        )}
                      />
                    </Container>
                  )}
                  {/* Synchronization message or Score breakdown + Questions */}
                  {!statusesMatch && polledCurrentAudit && displayAuditSummary?.audit ? (
                    <Container data-testid="syncing-container">
                      <Flashbar
                        data-testid="syncing-flashbar"
                        items={[
                          {
                            type: 'info',
                            loading: true,
                            header: 'Synchronizing Audit Data',
                            content: 'This may take a few moments.',
                            id: 'syncing',
                          },
                        ]}
                      />
                    </Container>
                  ) : statusesMatch ? (
                    <>
                      {/* Score breakdown with filtering - only show when synchronized */}
                      <ScoreBreakdown
                        data-testid="score-breakdown"
                        auditSummary={displayAuditSummary}
                        selectedFilter={summaryFilter}
                        onFilterChange={(filter) => {
                          setSummaryFilter(filter);
                          setSummaryPageIndex(1); // Reset to first page when filter changes
                        }}
                        allQuestions={allQuestions}
                      />
                      {/* Questions loading state */}
                      {questionsLoading && (
                        <Alert data-testid="questions-loading-alert" type="info">
                          Loading all questions... ({totalQuestionsLoaded} loaded)
                        </Alert>
                      )}
                      {/* Questions error state */}
                      {questionsError && (
                        <Alert data-testid="questions-error-alert" type="error">
                          Error loading questions: {questionsError}
                        </Alert>
                      )}
                      {/* Questions table with pagination - only show when synchronized */}
                      {hasLoadedAll && allQuestions.length > 0 && (
                        <QuestionsTable
                          data-testid="questions-table"
                          audit={displayAuditSummary!.audit}
                          questions={summaryPaginatedQuestions}
                          selectedFilter={summaryFilter}
                          isLoading={questionsLoading}
                          showLanguageDropdown={true}
                          paginationControls={
                            <Pagination
                              data-testid="pagination-summary"
                              currentPageIndex={summaryPageIndex}
                              pagesCount={summaryTotalPages}
                              onChange={({ detail }) =>
                                setSummaryPageIndex(detail.currentPageIndex)
                              }
                            />
                          }
                        />
                      )}
                      {/* Empty state when no questions */}
                      {hasLoadedAll && allQuestions.length === 0 ? (
                        displayAuditSummary?.audit.status === AuditStatus.SCHEDULED ? (
                          <Alert data-testid="scheduled-audit-alert" type="info">
                            Audit questions will be available after the audit moves into
                            "Not Started" status.
                          </Alert>
                        ) : (
                          <Alert data-testid="no-questions-alert" type="info">
                            No questions found for this audit.
                          </Alert>
                        )
                      ) : null}
                    </>
                  ) : null}
                </SpaceBetween>
              ),
            },
            {
              id: 'question-response',
              label: 'Question Response',
              content:
                !statusesMatch && polledCurrentAudit && displayAuditSummary?.audit ? (
                  <Container data-testid="syncing-container-question-response">
                    <Flashbar
                      data-testid="syncing-flashbar-question-response"
                      items={[
                        {
                          type: 'info',
                          loading: true,
                          header: 'Synchronizing Audit Data',
                          content: 'This may take a few moments.',
                          id: 'syncing-question-response',
                        },
                      ]}
                    />
                  </Container>
                ) : statusesMatch ? (
                  <SpaceBetween data-testid="question-response-tab-content" size="m">
                    <ScoreBreakdown
                      data-testid="question-response-score-breakdown"
                      auditSummary={displayAuditSummary}
                      selectedFilter={questionResponseFilter}
                      onFilterChange={(filter) => {
                        setQuestionResponseFilter(filter);
                        setQuestionResponsePageIndex(1); // Reset to first page when filter changes
                      }}
                      allQuestions={allQuestions}
                    />
                    {/* Questions loading state */}
                    {questionsLoading && (
                      <Alert data-testid="questions-loading-alert-qr" type="info">
                        Loading all questions... ({totalQuestionsLoaded} loaded)
                      </Alert>
                    )}
                    {/* Questions error state */}
                    {questionsError && (
                      <Alert data-testid="questions-error-alert-qr" type="error">
                        Error loading questions: {questionsError}
                      </Alert>
                    )}
                    {/* Questions table with pagination */}
                    {hasLoadedAll && allQuestions.length > 0 && (
                      <QuestionSummaryTable
                        data-testid="question-summary-table"
                        questions={questionResponsePaginatedQuestions}
                        isLoading={questionsLoading}
                        audit={displayAuditSummary!.audit}
                        tasks={tasksList}
                        selectedFilter={questionResponseFilter}
                        showLanguageDropdown={true}
                        paginationControls={
                          <Pagination
                            data-testid="pagination-question-response"
                            currentPageIndex={questionResponsePageIndex}
                            pagesCount={questionResponseTotalPages}
                            onChange={({ detail }) =>
                              setQuestionResponsePageIndex(detail.currentPageIndex)
                            }
                          />
                        }
                        onTaskCreated={() => {
                          loadTasks({
                            businessUnitId: businessUnit,
                            auditId: auditId,
                            taskStatus: [
                              TaskStatus.ASSIGNED,
                              TaskStatus.IN_PROGRESS,
                              TaskStatus.COMPLETED,
                              TaskStatus.CANCELLED,
                              TaskStatus.OVERDUE,
                            ],
                          });
                        }}
                      />
                    )}
                    {/* Empty state when no questions */}
                    {hasLoadedAll && allQuestions.length === 0 ? (
                      displayAuditSummary?.audit.status === AuditStatus.SCHEDULED ? (
                        <Alert data-testid="scheduled-audit-alert" type="info">
                          Audit questions will be available after the audit moves into
                          "Not Started" status.
                        </Alert>
                      ) : (
                        <Alert data-testid="no-questions-alert" type="info">
                          No questions found for this audit.
                        </Alert>
                      )
                    ) : null}
                  </SpaceBetween>
                ) : null,
            },
            {
              id: 'task',
              label: 'Task',
              content: (
                <TasksTable
                  data-testid="tasks-table"
                  tasks={tasksList}
                  fetchTasksApiStatus={fetchTasksApiStatus}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>
    </Box>
  );
};

/**
 * AuditSummaryPage - Reads the audit coordinates from the query string
 * Redirects to the audit list when either coordinate is absent.
 */
const AuditSummaryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get('auditId');
  const businessUnit = searchParams.get('businessUnit');

  useEffect(() => {
    if (!auditId || !businessUnit) {
      navigate(routes.AUDITS.path, { replace: true });
    }
  }, [auditId, businessUnit, navigate]);

  // Mounting the content without both coordinates would fetch on null ids.
  if (!auditId || !businessUnit) {
    return null;
  }

  return <AuditSummaryContent auditId={auditId} businessUnit={businessUnit} />;
};

export default AuditSummaryPage;
